<template>
    <div
        class="container-fluid corporate-overview-page weeduc-admin-panel page-card text-left p-0"
        :class="{ 'corporate-overview-page--dark': dark }"
    >
        <header class="corporate-overview-header">
            <div class="corporate-overview-header__lead">
                <TitlePage
                    :title="t.overview.title"
                    :subtitle="t.overview.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="corporate-overview-header__actions">
                <v-select
                    v-if="showMockPermission"
                    v-model="mockPermission"
                    :items="permissionOptions"
                    item-title="text"
                    item-value="value"
                    :label="t.permissions.label"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="weeduc-admin-v-field corporate-overview-permission"
                    style="min-width: 180px"
                    @update:model-value="applyMockPermission"
                />
                <v-btn
                    v-if="canPreview"
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="openPreviewAs = true"
                >
                    <v-icon class="me-1" size="small">mdi-eye-outline</v-icon>
                    {{ t.menu.previewAs }}
                </v-btn>
                <v-btn
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="$router.push('/dashboard/corporativo/estrutura')"
                >
                    <v-icon class="me-1" size="small">mdi-sitemap</v-icon>
                    {{ t.overview.goStructure }}
                </v-btn>
                <v-btn
                    variant="flat"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="$router.push('/dashboard/corporativo/colaboradores')"
                >
                    <v-icon class="me-1" size="small">mdi-account-group</v-icon>
                    {{ t.overview.goCollaborators }}
                </v-btn>
            </div>
        </header>

        <PreviewAsDialog v-model="openPreviewAs" />

        <div class="corporate-overview-shell">
            <div v-if="loading" class="corporate-overview-loading">
                <Loader :isActive="false" />
            </div>

            <div v-else-if="error" class="corporate-overview-empty">
                {{ t.common.error }}
            </div>

            <template v-else>
                <section class="corporate-overview-stats" :aria-label="t.common.summary">
                    <article
                        v-for="card in primaryStats"
                        :key="card.key"
                        class="corporate-overview-stat"
                        :class="{ 'corporate-overview-stat--clickable': card.to }"
                        role="link"
                        tabindex="0"
                        @click="goTo(card.to)"
                        @keydown.enter.prevent="goTo(card.to)"
                    >
                        <div
                            class="corporate-overview-stat__icon"
                            :class="`corporate-overview-stat__icon--${card.tone}`"
                            aria-hidden="true"
                        >
                            <fa :icon="card.icon" />
                        </div>
                        <div class="corporate-overview-stat__body">
                            <span class="corporate-overview-stat__label">{{ card.label }}</span>
                            <strong class="corporate-overview-stat__value">{{ card.value }}</strong>
                        </div>
                        <fa
                            v-if="card.to"
                            icon="fa-arrow-right"
                            class="corporate-overview-stat__arrow"
                        />
                    </article>
                </section>

                <section class="corporate-overview-secondary-stats" :aria-label="t.common.secondaryKpiIndicators">
                    <article
                        v-for="card in secondaryStats"
                        :key="card.key"
                        class="corporate-overview-mini"
                        :class="{ 'corporate-overview-mini--clickable': card.to }"
                        role="link"
                        tabindex="0"
                        @click="goTo(card.to)"
                        @keydown.enter.prevent="goTo(card.to)"
                    >
                        <span class="corporate-overview-mini__label">{{ card.label }}</span>
                        <strong class="corporate-overview-mini__value">{{ card.value }}</strong>
                        <fa
                            v-if="card.to"
                            icon="fa-arrow-right"
                            class="corporate-overview-mini__arrow"
                        />
                    </article>
                </section>

                <div class="corporate-overview-columns">
                    <section class="corporate-overview-column corporate-overview-column--main">
                        <div class="corporate-overview-block corporate-overview-block--fill">
                            <div class="corporate-overview-block__head corporate-overview-block__head--row">
                                <div>
                                    <h2 class="corporate-overview-block__title">{{ t.overview.ranking }}</h2>
                                    <p class="corporate-overview-block__lead">{{ t.overview.rankingLead }}</p>
                                </div>
                                <button
                                    type="button"
                                    class="corporate-overview-link-btn"
                                    @click="$router.push('/dashboard/corporativo/colaboradores')"
                                >
                                    {{ t.overview.seeCollaborators }}
                                    <fa icon="fa-arrow-right" />
                                </button>
                            </div>

                            <div v-if="!(overview.ranking || []).length" class="corporate-overview-empty">
                                {{ t.overview.emptyActivity }}
                            </div>

                            <div v-else class="corporate-overview-ranking">
                                <article
                                    v-for="row in overview.ranking"
                                    :key="row.userId"
                                    class="corporate-overview-rank-card"
                                    :class="{ 'corporate-overview-rank-card--top': row.position <= 3 }"
                                    @click="$router.push(`/dashboard/corporativo/colaboradores/${row.userId}`)"
                                >
                                    <div
                                        class="corporate-overview-rank-card__pos"
                                        :class="`corporate-overview-rank-card__pos--${row.position}`"
                                    >
                                        {{ row.position }}
                                    </div>
                                    <div class="corporate-overview-rank-card__body">
                                        <strong class="corporate-overview-rank-card__name">{{ row.name }}</strong>
                                        <span class="corporate-overview-rank-card__meta">
                                            {{ row.sectorName }} · {{ row.roleName }}
                                        </span>
                                    </div>
                                    <div class="corporate-overview-rank-card__score">
                                        <strong>{{ row.points }}</strong>
                                        <span>{{ t.overview.points }}</span>
                                        <em>{{ row.level }}</em>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </section>

                    <aside class="corporate-overview-column corporate-overview-column--side">
                        <div class="corporate-overview-block">
                            <div class="corporate-overview-block__head">
                                <h2 class="corporate-overview-block__title">{{ t.overview.bestSectors }}</h2>
                                <p class="corporate-overview-block__lead">{{ t.overview.sectorsLead }}</p>
                            </div>
                            <div class="corporate-overview-sector-list">
                                <div
                                    v-for="sector in overview.bestSectors || []"
                                    :key="'best-' + sector.id"
                                    class="corporate-overview-sector corporate-overview-sector--clickable"
                                    role="link"
                                    tabindex="0"
                                    @click="goTo('/dashboard/corporativo/estrutura')"
                                    @keydown.enter.prevent="goTo('/dashboard/corporativo/estrutura')"
                                >
                                    <div class="corporate-overview-sector__row">
                                        <span>{{ sector.name }}</span>
                                        <strong>{{ sector.completionRate }}%</strong>
                                    </div>
                                    <div class="corporate-overview-sector__bar">
                                        <span :style="{ width: sector.completionRate + '%' }" />
                                    </div>
                                    <small>
                                        {{ sector.usersCount }} {{ t.overview.usersInSector }}
                                    </small>
                                </div>
                            </div>
                        </div>

                        <div class="corporate-overview-block">
                            <div class="corporate-overview-block__head">
                                <h2 class="corporate-overview-block__title">{{ t.overview.worstSectors }}</h2>
                            </div>
                            <div class="corporate-overview-sector-list">
                                <div
                                    v-for="sector in overview.worstSectors || []"
                                    :key="'worst-' + sector.id"
                                    class="corporate-overview-sector corporate-overview-sector--warn corporate-overview-sector--clickable"
                                    role="link"
                                    tabindex="0"
                                    @click="goTo('/dashboard/corporativo/estrutura')"
                                    @keydown.enter.prevent="goTo('/dashboard/corporativo/estrutura')"
                                >
                                    <div class="corporate-overview-sector__row">
                                        <span>{{ sector.name }}</span>
                                        <strong>{{ sector.completionRate }}%</strong>
                                    </div>
                                    <div class="corporate-overview-sector__bar">
                                        <span :style="{ width: sector.completionRate + '%' }" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="corporate-overview-block">
                            <div class="corporate-overview-block__head corporate-overview-block__head--row">
                                <h2 class="corporate-overview-block__title">{{ t.overview.recentActivity }}</h2>
                            </div>
                            <div
                                v-if="!(overview.recentActivity || []).length"
                                class="corporate-overview-empty corporate-overview-empty--compact"
                            >
                                {{ t.overview.emptyActivity }}
                            </div>
                            <ul v-else class="corporate-overview-activity">
                                <li
                                    v-for="activity in overview.recentActivity"
                                    :key="activity.id"
                                    class="corporate-overview-activity__item"
                                    :class="{
                                        'corporate-overview-activity__item--clickable':
                                            activity.userId || activity.to,
                                    }"
                                    @click="goToActivity(activity)"
                                >
                                    <span class="corporate-overview-activity__dot" />
                                    <div>
                                        <p>{{ activity.description }}</p>
                                        <small>{{ formatDate(activity.createdAt) }}</small>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </aside>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import PreviewAsDialog from '@/components/Admin/Corporate/PreviewAsDialog.vue';
import corporateDashboard from '@/utils/corporate/dashboard';
import { corporateI18n } from '@/i18n/mixins';
import { mapActions, mapState } from 'vuex';
import { isCorporateMockEnabled } from '@/utils/corporate/environment';
import {
    canPreviewAsCollaborator,
    getCorporatePermission,
} from '@/utils/corporate/permissions';

export default {
    name: 'CorporateOverview',
    mixins: [corporateI18n],
    components: { TitlePage, Loader, PreviewAsDialog },
    data() {
        return {
            loading: true,
            error: false,
            overview: {},
            openPreviewAs: false,
            mockPermission: getCorporatePermission() || 'gestor',
        };
    },
    computed: {
        ...mapState(['token', 'dark', 'user']),
        showMockPermission() {
            return isCorporateMockEnabled();
        },
        canPreview() {
            return canPreviewAsCollaborator();
        },
        permissionOptions() {
            return [
                { text: this.t.permissions.gestor, value: 'gestor' },
                { text: this.t.permissions.coordenador, value: 'coordenador' },
                { text: this.t.permissions.colaborador, value: 'colaborador' },
            ];
        },
        totals() {
            return this.overview.totals || {};
        },
        primaryStats() {
            return [
                {
                    key: 'collaborators',
                    label: this.t.overview.collaborators,
                    value: this.totals.collaborators ?? '—',
                    icon: 'fa-people-group',
                    tone: 'primary',
                    to: '/dashboard/corporativo/colaboradores',
                },
                {
                    key: 'completion',
                    label: this.t.overview.completion,
                    value: `${this.totals.completionRate ?? 0}%`,
                    icon: 'fa-chart-line',
                    tone: 'teal',
                    to: '/dashboard/corporativo/acompanhamento',
                },
                {
                    key: 'inProgress',
                    label: this.t.overview.inProgress,
                    value: this.totals.enrolmentsInProgress ?? '—',
                    icon: 'fa-play',
                    tone: 'violet',
                    to: '/dashboard/corporativo/acompanhamento',
                },
            ];
        },
        secondaryStats() {
            return [
                {
                    key: 'sectors',
                    label: this.t.overview.sectors,
                    value: this.totals.sectors ?? '—',
                    to: '/dashboard/corporativo/estrutura',
                },
                {
                    key: 'roles',
                    label: this.t.overview.roles,
                    value: this.totals.roles ?? '—',
                    to: '/dashboard/corporativo/cargos',
                },
                {
                    key: 'courses',
                    label: this.t.overview.courses,
                    value: this.totals.corporateCourses ?? '—',
                    to: '/dashboard/cursos',
                },
                {
                    key: 'active',
                    label: this.t.overview.activeCollaborators,
                    value: this.totals.activeCollaborators ?? '—',
                    to: '/dashboard/corporativo/colaboradores',
                },
                {
                    key: 'enrolments',
                    label: this.t.overview.enrolments,
                    value: this.totals.totalEnrolments ?? '—',
                    to: '/dashboard/corporativo/acompanhamento',
                },
                {
                    key: 'completed',
                    label: this.t.overview.completed,
                    value: this.totals.enrolmentsCompleted ?? '—',
                    to: '/dashboard/corporativo/acompanhamento',
                },
            ];
        },
    },
    async created() {
        try {
            this.overview = await corporateDashboard.getOverview(this.token);
        } catch (e) {
            console.error(e);
            this.error = true;
        } finally {
            this.loading = false;
        }
    },
    methods: {
        ...mapActions(['storeUser']),
        applyMockPermission(value) {
            const nextUser = {
                ...(this.user || {}),
                corporatePermission: value,
            };
            this.storeUser({ user: nextUser });
            this.mockPermission = value;
            if (value === 'colaborador') {
                this.$router.push('/aluno/dashboard');
            }
        },
        goTo(path) {
            if (!path) return;
            this.$router.push(path);
        },
        goToActivity(activity) {
            if (!activity) return;
            if (activity.to) {
                this.goTo(activity.to);
                return;
            }
            if (activity.userId) {
                this.goTo(`/dashboard/corporativo/colaboradores/${activity.userId}`);
                return;
            }
            this.goTo('/dashboard/corporativo/colaboradores');
        },
        formatDate(value) {
            if (!value) return '';
            try {
                return new Intl.DateTimeFormat('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                }).format(new Date(value));
            } catch (e) {
                return value;
            }
        },
    },
};
</script>

<style scoped>
.corporate-overview-page {
    background: #f4f5f8;
    border-radius: 16px;
    padding: 0 0 1rem;
    box-shadow: 0 1px 3px rgba(37, 8, 145, 0.06);
    width: 100%;
}

.corporate-overview-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem 1.5rem;
    padding: 1.25rem 1.5rem 1rem;
    background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
    border-bottom: 1px solid #e8eaf0;
    border-radius: 16px 16px 0 0;
}

.corporate-overview-header__lead {
    flex: 1;
    min-width: 200px;
}

.corporate-overview-header__lead :deep(.titlelayoutGeneric) {
    border-bottom: none;
    padding-bottom: 0;
}

.corporate-overview-header__lead :deep(.titlelayoutGeneric h3) {
    font-size: 1.5rem;
    margin: 0 0 0.35rem;
    color: #1a1d26;
}

.corporate-overview-header__lead :deep(.titlelayoutGeneric small) {
    display: block;
    color: #5c6370;
    line-height: 1.45;
}

.corporate-overview-header__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
}

.corporate-overview-shell {
    padding: 1.25rem 1.5rem 1.5rem;
    height: calc(100vh - 210px);
    overflow-y: auto;
    min-height: 0;
    box-sizing: border-box;
    scrollbar-width: none;
}

.corporate-overview-shell::-webkit-scrollbar {
    display: none;
}

.corporate-overview-loading,
.corporate-overview-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    color: #5c6370;
    text-align: center;
}

.corporate-overview-empty--compact {
    min-height: 80px;
    font-size: 0.875rem;
}

.corporate-overview-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
}

.corporate-overview-stat {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 1rem 1.15rem;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
    position: relative;
}

.corporate-overview-stat--clickable {
    cursor: pointer;
}

.corporate-overview-stat--clickable:hover {
    border-color: rgba(37, 8, 145, 0.35);
    box-shadow: 0 4px 14px rgba(37, 8, 145, 0.08);
    transform: translateY(-2px);
}

.corporate-overview-stat__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #eef0fc;
}

.corporate-overview-stat__icon--primary {
    background: #eef0fc;
    color: #250891;
}

.corporate-overview-stat__icon--teal {
    background: rgba(26, 199, 208, 0.15);
    color: #0e8f96;
}

.corporate-overview-stat__icon--violet {
    background: rgba(57, 36, 151, 0.12);
    color: #392497;
}

.corporate-overview-stat__icon--danger {
    background: rgba(198, 40, 40, 0.1);
    color: #c62828;
}

.corporate-overview-stat__icon :deep(svg) {
    width: 1.15rem !important;
    height: 1.15rem !important;
}

.corporate-overview-stat__body {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
    text-align: left;
}

.corporate-overview-stat__label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5c6370;
}

.corporate-overview-stat__value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #160266;
    line-height: 1.15;
}

.corporate-overview-stat__arrow {
    margin-left: auto;
    color: #250891;
    opacity: 0.45;
    font-size: 0.75rem;
}

.corporate-overview-secondary-stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.25rem;
}

.corporate-overview-mini {
    position: relative;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    padding: 0.85rem 1rem;
    text-align: left;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.corporate-overview-mini--clickable {
    cursor: pointer;
}

.corporate-overview-mini--clickable:hover {
    border-color: rgba(37, 8, 145, 0.35);
    box-shadow: 0 4px 14px rgba(37, 8, 145, 0.08);
    transform: translateY(-2px);
}

.corporate-overview-mini__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #8a8f9c;
    margin-bottom: 0.25rem;
    padding-right: 1rem;
}

.corporate-overview-mini__value {
    font-size: 1.15rem;
    font-weight: 700;
    color: #1a1d26;
}

.corporate-overview-mini__arrow {
    position: absolute;
    top: 0.85rem;
    right: 0.85rem;
    color: #250891;
    opacity: 0.4;
    font-size: 0.65rem;
}

.corporate-overview-mini--clickable:hover .corporate-overview-mini__arrow {
    opacity: 0.8;
}

.corporate-overview-columns {
    display: flex;
    gap: 1.25rem;
    align-items: stretch;
}

.corporate-overview-column {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    min-width: 0;
}

.corporate-overview-column--side {
    flex: 0.38;
}

.corporate-overview-column--main {
    flex: 0.62;
}

.corporate-overview-block {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1.15rem 1.25rem 1.25rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.corporate-overview-block--fill {
    flex: 1;
}

.corporate-overview-block--access {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
}

.corporate-overview-block__head {
    margin-bottom: 1rem;
    padding-bottom: 0.85rem;
    border-bottom: 1px solid #f0f2f5;
}

.corporate-overview-block__head--row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.corporate-overview-block__title {
    margin: 0;
    font-size: 1rem;
    font-weight: 650;
    color: #1a1d26;
}

.corporate-overview-block__lead {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: #5c6370;
    line-height: 1.4;
}

.corporate-overview-status-list {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    margin-bottom: 1rem;
}

.corporate-overview-status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.875rem;
    color: #1a1d26;
}

.corporate-overview-status-row__meta {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.corporate-overview-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.corporate-overview-progress-track {
    display: flex;
    width: 100%;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: #eef0f3;
}

.corporate-overview-progress-track__seg {
    display: block;
    height: 100%;
}

.corporate-overview-link-btn {
    border: none;
    background: transparent;
    color: #250891;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    white-space: nowrap;
    padding: 0.25rem 0;
}

.corporate-overview-ranking {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}

.corporate-overview-rank-card {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.85rem 0.95rem;
    border: 1px solid #eef0f3;
    border-radius: 12px;
    background: #fafbfc;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.corporate-overview-rank-card:hover {
    border-color: rgba(37, 8, 145, 0.28);
    box-shadow: 0 4px 12px rgba(37, 8, 145, 0.06);
    transform: translateY(-1px);
}

.corporate-overview-rank-card--top {
    background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
}

.corporate-overview-rank-card__pos {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
    background: #eef0fc;
    color: #250891;
    flex-shrink: 0;
}

.corporate-overview-rank-card__pos--1 {
    background: #fff4d6;
    color: #9a6b00;
}

.corporate-overview-rank-card__pos--2 {
    background: #eef1f5;
    color: #4b5563;
}

.corporate-overview-rank-card__pos--3 {
    background: #f3e6d8;
    color: #8a4b1f;
}

.corporate-overview-rank-card__body {
    flex: 1;
    min-width: 0;
    text-align: left;
}

.corporate-overview-rank-card__name {
    display: block;
    font-size: 0.95rem;
    color: #1a1d26;
}

.corporate-overview-rank-card__meta {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.75rem;
    color: #5c6370;
}

.corporate-overview-rank-card__score {
    text-align: right;
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    flex-shrink: 0;
}

.corporate-overview-rank-card__score strong {
    font-size: 1.05rem;
    color: #160266;
}

.corporate-overview-rank-card__score span {
    font-size: 0.68rem;
    color: #8a8f9c;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.corporate-overview-rank-card__score em {
    font-style: normal;
    font-size: 0.75rem;
    font-weight: 600;
    color: #0e8f96;
}

.corporate-overview-sector-list {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.corporate-overview-sector--clickable {
    cursor: pointer;
    border-radius: 10px;
    padding: 0.35rem 0.25rem;
    margin: 0 -0.25rem;
    transition: background 0.15s ease;
}

.corporate-overview-sector--clickable:hover {
    background: rgba(37, 8, 145, 0.04);
}

.corporate-overview-activity__item--clickable {
    cursor: pointer;
    border-radius: 10px;
    transition: background 0.15s ease;
}

.corporate-overview-activity__item--clickable:hover {
    background: rgba(37, 8, 145, 0.04);
}

.corporate-overview-sector__row {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.875rem;
    color: #1a1d26;
    margin-bottom: 0.35rem;
}

.corporate-overview-sector__bar {
    width: 100%;
    height: 7px;
    border-radius: 999px;
    background: #eef0f3;
    overflow: hidden;
}

.corporate-overview-sector__bar span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #250891 0%, #1ac7d0 100%);
}

.corporate-overview-sector--warn .corporate-overview-sector__bar span {
    background: linear-gradient(90deg, #c62828 0%, #ef6c00 100%);
}

.corporate-overview-sector small {
    display: block;
    margin-top: 0.3rem;
    font-size: 0.72rem;
    color: #8a8f9c;
}

.corporate-overview-activity {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.corporate-overview-activity li {
    display: flex;
    gap: 0.7rem;
    align-items: flex-start;
}

.corporate-overview-activity__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    margin-top: 0.35rem;
    background: #1ac7d0;
    flex-shrink: 0;
    box-shadow: 0 0 0 3px rgba(26, 199, 208, 0.18);
}

.corporate-overview-activity p {
    margin: 0;
    font-size: 0.85rem;
    color: #1a1d26;
    line-height: 1.35;
    text-align: left;
}

.corporate-overview-activity small {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.72rem;
    color: #8a8f9c;
}

@media (max-width: 1200px) {
    .corporate-overview-stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .corporate-overview-secondary-stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .corporate-overview-columns {
        flex-direction: column;
    }

    .corporate-overview-column--side,
    .corporate-overview-column--main {
        flex: 1;
    }
}

@media (max-width: 767px) {
    .corporate-overview-stats,
    .corporate-overview-secondary-stats {
        grid-template-columns: 1fr;
    }

    .corporate-overview-header {
        padding: 1rem;
    }

    .corporate-overview-shell {
        padding: 1rem;
        height: auto;
        max-height: none;
    }
}

.dashboard.dark .corporate-overview-page,
.corporate-overview-page--dark {
    background: #13121a;
    box-shadow: none;
}

.dashboard.dark .corporate-overview-header,
.corporate-overview-page--dark .corporate-overview-header {
    background: linear-gradient(135deg, #1c1a24 0%, #201e26 100%);
    border-bottom-color: #2d2d2d;
}

.dashboard.dark .corporate-overview-header__lead :deep(.titlelayoutGeneric h3),
.dashboard.dark .corporate-overview-stat__value,
.dashboard.dark .corporate-overview-mini__value,
.dashboard.dark .corporate-overview-block__title,
.dashboard.dark .corporate-overview-rank-card__name,
.dashboard.dark .corporate-overview-sector__row,
.dashboard.dark .corporate-overview-activity p,
.dashboard.dark .corporate-overview-status-row {
    color: #f6f6f6;
}

.dashboard.dark .corporate-overview-stat,
.dashboard.dark .corporate-overview-mini,
.dashboard.dark .corporate-overview-block:not(.corporate-overview-block--access),
.dashboard.dark .corporate-overview-rank-card {
    background: #201e26;
    border-color: #2d2d2d;
}

.dashboard.dark .corporate-overview-rank-card,
.dashboard.dark .corporate-overview-block__head {
    border-bottom-color: #2d2d2d;
}

.dashboard.dark .corporate-overview-rank-card--top {
    background: linear-gradient(135deg, #2a2740 0%, #201e26 100%);
}

.dashboard.dark .corporate-overview-rank-card:hover {
    border-color: rgba(165, 180, 252, 0.35);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.dashboard.dark .corporate-overview-rank-card__pos {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-overview-rank-card__pos--1 {
    background: rgba(255, 244, 214, 0.18);
    color: #fcd34d;
}

.dashboard.dark .corporate-overview-rank-card__pos--2 {
    background: rgba(200, 210, 230, 0.15);
    color: #cbd5e1;
}

.dashboard.dark .corporate-overview-rank-card__pos--3 {
    background: rgba(205, 150, 100, 0.18);
    color: #fdba74;
}

.dashboard.dark .corporate-overview-rank-card__meta,
.dashboard.dark .corporate-overview-rank-card__score span,
.dashboard.dark .corporate-overview-sector small,
.dashboard.dark .corporate-overview-activity small {
    color: #9ca3b4;
}

.dashboard.dark .corporate-overview-rank-card__score strong {
    color: #e8eaf0;
}

.dashboard.dark .corporate-overview-rank-card__score em {
    color: #5eead4;
}

.dashboard.dark .corporate-overview-sector__bar {
    background: #353945;
}

.dashboard.dark .corporate-overview-sector--clickable:hover,
.dashboard.dark .corporate-overview-activity__item--clickable:hover {
    background: rgba(165, 180, 252, 0.08);
}

.dashboard.dark .corporate-overview-header__actions :deep(.v-btn--variant-tonal),
.dashboard.dark .corporate-overview-header__actions :deep(.v-btn--variant-outlined) {
    color: #e8eaf0 !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: #3d4150;
}

.dashboard.dark .corporate-overview-stat__icon--primary {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-overview-stat__icon--teal {
    background: rgba(26, 199, 208, 0.18);
    color: #5eead4;
}

.dashboard.dark .corporate-overview-stat__icon--violet {
    background: rgba(165, 180, 252, 0.15);
    color: #a5b4fc;
}

.dashboard.dark .corporate-overview-stat__icon--danger {
    background: rgba(198, 40, 40, 0.2);
    color: #fca5a5;
}

.dashboard.dark .corporate-overview-mini__icon {
    color: #c4b5fd;
}

.dashboard.dark .corporate-overview-stat__arrow {
    color: #c4b5fd;
    opacity: 0.7;
}

</style>
